/**
 * SoleadLab 2.0 — Field Actions Panel (FL-1)
 *
 * The write surface for FIELD-kind share-link visitors (technicians without
 * accounts). Renders as a floating card over the map whenever an indication
 * is selected (same trigger as IndicationPreview) and the active session is
 * a field link — for everyone else it renders nothing.
 *
 * Deliberately a SEPARATE surface from IndicationPreview/IndicationPopup:
 * the global read-only contract (useIsReadOnlyViewer) stays true for field
 * visitors, so none of the F-02E-2B viewer gates are reopened. The only
 * writes here go through the two FL-1 store actions, which the backend
 * enforces independently (kind check, actor header, rate limit, allowlist).
 *
 * Identity: the technician's self-reported name is required by the backend
 * on every write (X-SL-Field-Actor — attached by apiFetch). First use asks
 * once; the name is remembered per device per link (utils/fieldActor).
 */

import { useRef, useState } from 'react';
import { useProjectStore, useUIStore, useMapStore, STATUS_LABELS, STATUS_ORDER, INDICATION_TYPES } from '../../stores';
import { useFieldCapabilities } from '../../hooks/useShareViewer';
import { getFieldActor, setFieldActor } from '../../utils/fieldActor';
import { downscaleImageToJpeg } from '../../utils/downscaleImage';
import FieldPhotosStrip from './FieldPhotosStrip';
import './FieldActionsPanel.css';

type StatusValue = (typeof STATUS_ORDER)[number];

interface FieldActionsPanelProps {
  indicationId: string;
}

export default function FieldActionsPanel({ indicationId }: FieldActionsPanelProps) {
  const caps = useFieldCapabilities();
  const indication = useProjectStore((s) =>
    s.data.indications.find((i) => i.id === indicationId),
  );
  const setIndicationStatus = useProjectStore((s) => s.setIndicationStatus);
  const setIndicationFieldNotes = useProjectStore((s) => s.setIndicationFieldNotes);
  const removeIndicationAsField = useProjectStore((s) => s.removeIndicationAsField);
  const addIndicationFieldPhoto = useProjectStore((s) => s.addIndicationFieldPhoto);
  const removeIndicationFieldPhoto = useProjectStore((s) => s.removeIndicationFieldPhoto);
  const addNotification = useUIStore((s) => s.addNotification);
  // Flow C auto-attach in flight for THIS indication (utils/fieldPhotoFlow):
  // treat like our own busy so a status tap can't 409 against the append.
  const autoUploading = useUIStore((s) => s.fieldPhotoUploading) === indicationId;

  const storedActor = caps.linkId ? getFieldActor(caps.linkId) : null;
  const [actorDraft, setActorDraft] = useState(storedActor ?? '');
  const [actorConfirmed, setActorConfirmed] = useState(!!storedActor);
  const [editingActor, setEditingActor] = useState(false);
  const [notesDraft, setNotesDraft] = useState(
    (indication?.properties.description as string | undefined) ?? '',
  );
  const [notesFor, setNotesFor] = useState(indicationId);
  const [busy, setBusy] = useState(false);
  // FL-3a: sequential multi-photo upload progress ("Uploading… 2/5"). `for`
  // scopes the label to the indication the batch actually targets — the
  // panel stays mounted across selection changes, and the loop keeps writing
  // to the ORIGINAL id (correct); the new card must not wear its progress.
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number; for: string } | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  // Interaction-model contract (2026-08-13 coordination doc): vertex editing
  // is reachable ONLY through this card's Reshape button — never from
  // selection or vertex clicks. This flag drives the button's Done state.
  const [reshaping, setReshaping] = useState(false);

  // Selection changed → reload the notes draft for the new indication.
  if (notesFor !== indicationId) {
    setNotesFor(indicationId);
    setNotesDraft((indication?.properties.description as string | undefined) ?? '');
    setReshaping(false);
  }

  if (!caps.canSetStatus || !caps.linkId || !indication) return null;

  const linkId = caps.linkId;
  const blocked = busy || autoUploading;
  const currentStatus = ((indication.properties.status as string | undefined) ?? 'open') as StatusValue;
  const savedNotes = (indication.properties.description as string | undefined) ?? '';
  const typeLabel =
    (indication.type === 'Custom' && indication.properties.customTypeName) ||
    INDICATION_TYPES[indication.type]?.label ||
    indication.type;

  const confirmActor = () => {
    const trimmed = actorDraft.trim();
    if (!trimmed) return;
    setFieldActor(linkId, trimmed);
    setActorConfirmed(true);
    setEditingActor(false);
  };

  const reportResult = (
    result: 'updated' | 'local' | 'conflict' | 'error' | 'noop',
    successTitle: string,
  ) => {
    if (result === 'updated') {
      addNotification({ type: 'success', title: successTitle });
    } else if (result === 'conflict') {
      addNotification({
        type: 'error',
        title: 'Someone else just changed this indication',
        message: 'The latest state was reloaded — please check and retry.',
      });
    } else if (result !== 'noop') {
      addNotification({
        type: 'error',
        title: "Couldn't save",
        message: 'If you made many changes quickly, wait a minute and try again.',
      });
    }
  };

  const handleStatus = async (status: StatusValue) => {
    if (blocked || status === currentStatus) return;
    setBusy(true);
    const result = await setIndicationStatus(indication.id, status);
    setBusy(false);
    reportResult(result, `Marked ${STATUS_LABELS[status]}`);
  };

  const handleSaveNotes = async () => {
    if (blocked || notesDraft === savedNotes) return;
    setBusy(true);
    const result = await setIndicationFieldNotes(indication.id, notesDraft);
    setBusy(false);
    reportResult(result, 'Note saved');
  };

  // FL-3a: sequential photo upload (downscale → attach), never clobbering —
  // the server appends to properties.fieldPhotos. Existing photos on the
  // indication (source photo or earlier field photos) are untouched.
  const handlePhotoFiles = async (list: FileList | null) => {
    if (!list || list.length === 0 || blocked) return;
    const files = Array.from(list);
    const targetId = indication.id;
    setBusy(true);
    setUploadProgress({ done: 0, total: files.length, for: targetId });
    let added = 0;
    let failed = 0;
    let limitHit = false;
    for (let i = 0; i < files.length; i++) {
      const { blob, name } = await downscaleImageToJpeg(files[i]);
      const result = await addIndicationFieldPhoto(targetId, blob, name);
      if (result === 'updated') added++;
      else if (result === 'limit') { limitHit = true; break; }
      else if (result !== 'noop') failed++;
      setUploadProgress({ done: i + 1, total: files.length, for: targetId });
    }
    setUploadProgress(null);
    setBusy(false);
    if (added > 0) {
      addNotification({
        type: 'success',
        title: added === 1 ? 'Photo added' : `${added} photos added`,
      });
    }
    if (limitHit) {
      addNotification({
        type: 'error',
        title: 'Photo limit reached for this mark',
        message: 'This indication already holds the maximum number of field photos.',
      });
    } else if (failed > 0) {
      addNotification({
        type: 'error',
        title: failed === 1 ? "Couldn't upload photo" : `Couldn't upload ${failed} photos`,
        message: 'Check your connection and try again. If you added many photos quickly, wait a minute.',
      });
    }
  };

  const handleRemovePhoto = async (photoId: string) => {
    if (blocked) return;
    setBusy(true);
    const result = await removeIndicationFieldPhoto(indication.id, photoId);
    setBusy(false);
    reportResult(result, 'Photo removed');
  };

  // FL-2.5: a field link owns what it created — only own marks are removable
  // (and movable/reshapable; the backend enforces the same rule).
  const isOwnMark = indication.properties.createdVia?.linkId === linkId;

  // Coordination-doc contract: Reshape enters direct_select for THIS feature;
  // Done returns to field_select. The draw.modechange guard (Session A) keeps
  // every other path into direct_select closed for share visitors.
  const toggleReshape = () => {
    const draw = useMapStore.getState().draw;
    if (!draw) return;
    try {
      if (reshaping) {
        draw.changeMode('field_select');
        setReshaping(false);
      } else {
        draw.changeMode('direct_select', { featureId: indication.id });
        setReshaping(true);
      }
    } catch {
      // Best-effort: Draw rejects the change if the feature isn't on the
      // canvas (e.g. zoomed out past the indication layer threshold).
      setReshaping(false);
    }
  };

  const handleRemove = async () => {
    if (blocked) return;
    if (!window.confirm('Remove this mark? Your reported finding will be deleted.')) return;
    setBusy(true);
    const result = await removeIndicationAsField(indication.id);
    setBusy(false);
    if (result === 'deleted') {
      addNotification({ type: 'success', title: 'Mark removed' });
    } else if (result !== 'noop') {
      addNotification({
        type: 'error',
        title: "Couldn't remove",
        message: 'If you made many changes quickly, wait a minute and try again.',
      });
    }
  };

  const needsName = !actorConfirmed || editingActor;

  return (
    <div className="field-actions" data-testid="field-actions-panel">
      <div className="field-actions__header">
        <span className="field-actions__title">Field update</span>
        <span className="field-actions__type">{typeLabel}</span>
      </div>

      {needsName ? (
        <div className="field-actions__identity" data-testid="field-actions-name-prompt">
          <label htmlFor="field-actor-name">Your name (shown on every change)</label>
          <div className="field-actions__identity-row">
            <input
              id="field-actor-name"
              type="text"
              maxLength={60}
              placeholder="e.g. John Doe"
              value={actorDraft}
              onChange={(e) => setActorDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmActor(); }}
            />
            <button
              type="button"
              className="field-actions__btn field-actions__btn--primary"
              disabled={!actorDraft.trim()}
              onClick={confirmActor}
              data-testid="field-actions-name-confirm"
            >
              Continue
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="field-actions__working-as">
            Working as <strong>{actorDraft.trim()}</strong>
            <button
              type="button"
              className="field-actions__link"
              onClick={() => setEditingActor(true)}
            >
              change
            </button>
          </div>

          <div className="field-actions__status" role="group" aria-label="Set status">
            {STATUS_ORDER.map((s) => (
              <button
                key={s}
                type="button"
                data-status={s}
                disabled={blocked}
                className={`field-actions__status-btn${currentStatus === s ? ' is-active' : ''}`}
                onClick={() => handleStatus(s)}
              >
                {STATUS_LABELS[s]}
              </button>
            ))}
          </div>

          <div className="field-actions__notes">
            <label htmlFor="field-actions-notes">Notes</label>
            <textarea
              id="field-actions-notes"
              rows={2}
              maxLength={10000}
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              placeholder="What did you find / fix?"
            />
          </div>

          <div className="field-actions__photos" data-testid="field-actions-photos">
            <FieldPhotosStrip
              photos={indication.properties.fieldPhotos ?? []}
              canRemoveLinkId={linkId}
              onRemove={handleRemovePhoto}
              busy={blocked}
            />
            {/* One row for all three actions — on a phone the card competes
                with the map for height, and stacked rows cost ~50px each. */}
            <div className="field-actions__action-row">
              <button
                type="button"
                className="field-actions__btn field-actions__btn--primary"
                disabled={blocked || notesDraft === savedNotes}
                onClick={handleSaveNotes}
                data-testid="field-actions-save-notes"
              >
                Save note
              </button>
              <button
                type="button"
                className="field-actions__btn field-actions__btn--primary"
                disabled={blocked}
                onClick={() => cameraInputRef.current?.click()}
                data-testid="field-actions-add-picture"
              >
                {uploadProgress && uploadProgress.for === indicationId
                  ? `Uploading… ${uploadProgress.done}/${uploadProgress.total}`
                  : autoUploading
                    ? 'Uploading…'
                    : '📷 Add picture'}
              </button>
              <button
                type="button"
                className="field-actions__btn"
                disabled={blocked}
                onClick={() => galleryInputRef.current?.click()}
                data-testid="field-actions-add-gallery"
              >
                Gallery
              </button>
            </div>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="field-actions__hidden-input"
              data-testid="field-actions-camera-input"
              onChange={(e) => {
                void handlePhotoFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              multiple
              className="field-actions__hidden-input"
              data-testid="field-actions-gallery-input"
              onChange={(e) => {
                void handlePhotoFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </div>

          {isOwnMark && (
            <div className="field-actions__own-row">
              <span className="field-actions__own-hint">
                {reshaping
                  ? 'Drag the corners on the map, then tap Done'
                  : 'Your mark — press and hold it to move'}
              </span>
              <button
                type="button"
                className={`field-actions__btn${reshaping ? ' field-actions__btn--primary' : ''}`}
                disabled={blocked}
                onClick={toggleReshape}
                data-testid="field-actions-reshape"
              >
                {reshaping ? 'Done' : 'Reshape'}
              </button>
              <button
                type="button"
                className="field-actions__btn field-actions__btn--danger"
                disabled={blocked || reshaping}
                onClick={handleRemove}
                data-testid="field-actions-remove"
              >
                Remove
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
